import React, { useEffect, useMemo, useState } from 'react';
import { useRestaurant } from '../hooks/useRestaurant';
import { KotTicket } from '../types';
import { formatTime } from '../services/datetime';
import './Sections.css';
import './Restaurant.css';

// Kitchen Display System (KDS): a live board of the KOTs the kitchen still has
// to cook. It updates in real time — a KOT fired from any captain tablet or the
// billing counter appears here within a second (useRestaurant reloads on the
// COLLECTION_CHANGED op event), and marking a ticket ready is reflected on the
// other terminals the same way. Works offline against local state; the sync
// layer fills in once the LAN server is reachable.
//
// It keys off `preparedAt` (set here) and `status` (the billing lifecycle): only
// active, non-cancelled tickets show, split into "Preparing" and "Ready".

const MINUTES = 60 * 1000;

// How long a ticket has been waiting, and a colour that escalates with age.
function ageInfo(iso: string, now: number): { mins: number; color: string } {
  const mins = Math.max(0, Math.floor((now - new Date(iso).getTime()) / MINUTES));
  const color = mins >= 15 ? '#d21f3c' : mins >= 8 ? '#d08a1a' : '#1a9e57';
  return { mins, color };
}

const KitchenDisplay: React.FC = () => {
  const rest = useRestaurant();
  // Re-render every 10s so the elapsed-time counters tick without any action.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(id);
  }, []);

  const { preparing, ready } = useMemo(() => {
    const live = rest.kots.filter((k) => k.status === 'active');
    return {
      // Oldest first while cooking (FIFO); most-recently-marked first once ready.
      preparing: live
        .filter((k) => !k.preparedAt)
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
      ready: live
        .filter((k) => k.preparedAt)
        .sort((a, b) => (b.preparedAt || '').localeCompare(a.preparedAt || '')),
    };
  }, [rest.kots]);

  const renderTicket = (k: KotTicket, done: boolean) => {
    const { mins, color } = ageInfo(k.timestamp, now);
    const totalQty = k.lines.reduce((q, l) => q + l.quantity, 0);
    return (
      <div
        key={k.id}
        className="kot-card"
        style={{
          borderLeft: `6px solid ${done ? '#9aa0a6' : color}`,
          opacity: done ? 0.7 : 1,
          marginBottom: 12,
        }}
      >
        <div className="kot-card-head" style={{ cursor: 'default' }}>
          <span className="kot-no">KOT #{k.kotNo}</span>
          <span className="kot-table" style={{ fontWeight: 700 }}>
            {k.tableName}
          </span>
          <span className="kot-waiter">
            {k.waiter || '—'} · {formatTime(k.timestamp)} · Qty {totalQty}
          </span>
          <span className="right" style={{ minWidth: 70, fontWeight: 700, color }}>
            {mins} min
          </span>
          <button
            className={done ? 'btn-grey' : 'btn-teal'}
            style={{ borderRadius: 20, minWidth: 110 }}
            onClick={() => rest.markKotPrepared(k.id, !done)}
          >
            {done ? 'Undo' : 'Mark Ready'}
          </button>
        </div>
        <div className="kot-card-lines">
          {k.lines.map((l, i) => (
            <div className="kl" key={i}>
              <span style={{ fontSize: 16 }}>
                <strong>{l.quantity}×</strong> {l.name}
                {l.remark && <em className="kl-remark"> — {l.remark}</em>}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="section-page rest-page">
      <div className="page-header">
        <h1>KITCHEN DISPLAY</h1>
        <span style={{ marginLeft: 'auto', fontWeight: 700 }}>
          {preparing.length} preparing · {ready.length} ready
        </span>
      </div>

      <div className="kds-columns" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 18, margin: '4px 0 12px' }}>Preparing ({preparing.length})</h2>
          {preparing.length === 0 ? (
            <p className="empty-hint">No tickets waiting. 🎉</p>
          ) : (
            preparing.map((k) => renderTicket(k, false))
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 18, margin: '4px 0 12px' }}>Ready ({ready.length})</h2>
          {ready.length === 0 ? (
            <p className="empty-hint">Nothing marked ready yet.</p>
          ) : (
            ready.map((k) => renderTicket(k, true))
          )}
        </div>
      </div>
    </div>
  );
};

export default KitchenDisplay;
